<script lang="ts">
import {Vue, Component, Watch} from 'vue-facing-decorator';
import {useColorStore, useLocaleStore} from "@/stores";
import {useRouter, useRoute} from "vue-router";
import { usePostHog } from '@/composables/usePostHog';

@Component({})
export default class NavBar extends Vue {
  private colorStore = useColorStore();
  private localeStore = useLocaleStore();
  private router = useRouter();
  private route = useRoute();
  private isMenuOpen = false;
  private posthog = usePostHog().posthog;

  mounted() {
    // Initialize i18n locale from store on mount
    this.$i18n.locale = this.localeStore.locale;
  }

  private get activeSection(): string | null {
    const name = this.route.name as string;
    if (name === 'home') return 'home';
    if (this.route.path.startsWith('/infrastructure')) return 'infra';
    if (['projects', 'pikuTrade', 'cgj', 'referendum', 'sproogy', 'ndi', 'portfolio'].includes(name)) return 'projects';
    if (name === 'about') return 'about';
    if (name === 'skills') return 'skills';
    if (name === 'resume') return 'resume';
    return null;
  }

  private toggleTheme() {
    this.colorStore.toggleTheme();
    this.posthog.capture('theme_toggled', { new_theme: this.colorStore.isLightTheme ? 'light' : 'dark' });
  }

  private toggleLocale() {
    this.localeStore.toggleLocale();
    this.$i18n.locale = this.localeStore.locale;
    this.posthog.capture('locale_toggled', { new_locale: this.localeStore.locale });
  }

  private navigateToHome() {
    this.posthog.capture('nav_link_clicked', { target: 'home' });
    this.router.push({name: 'home'});
  }

  private navigateToAbout() {
    this.posthog.capture('nav_link_clicked', { target: 'about' });
    this.router.push({name: 'about'});
  }
  private navigateToSkills() {
    this.posthog.capture('nav_link_clicked', { target: 'skills' });
    this.router.push({name: 'skills'});
  }

  private navigateToInfra() {
    this.posthog.capture('nav_link_clicked', { target: 'infrastructure' });
    this.router.push({ name: 'infrastructure' });
    this.closeMenu();
  }

  private navigateToProjects() {
    this.posthog.capture('nav_link_clicked', { target: 'projects' });
    this.router.push({ name: 'projects' });
    this.closeMenu();
  }

  private navigateToResume() {
    this.posthog.capture('nav_link_clicked', { target: 'resume' });
    this.router.push({name: 'resume'});
    this.closeMenu();
  }

  public trackSocialClick(platform: string) {
    this.posthog.capture('social_link_clicked', { platform: platform });
  }

  private toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    if (this.isMenuOpen) {
      this.posthog.capture('mobile_menu_opened');
    }
  }

  private closeMenu() {
    this.isMenuOpen = false;
  }
}
</script>

<template>
  <!-- Burger button (mobile only) -->
  <button class="burger-btn" :class="{ 'menu-open': isMenuOpen }" @click="toggleMenu" :aria-label="isMenuOpen ? 'Close menu' : 'Open menu'">
    <span class="burger-line" :class="{ 'open': isMenuOpen }"></span>
    <span class="burger-line" :class="{ 'open': isMenuOpen }"></span>
    <span class="burger-line" :class="{ 'open': isMenuOpen }"></span>
  </button>

  <!-- Backdrop (mobile only) -->
  <div class="backdrop" :class="{ 'active': isMenuOpen }" @click="closeMenu"></div>

  <!-- Main header/sidebar -->
  <header class="header" :class="{ 'mobile-open': isMenuOpen }">
    <div class="top">
      <a @click="navigateToHome(); closeMenu()" 
         class="mdi mdi-home" 
         :class="{ 'active': activeSection === 'home' }"
         v-tooltip="$t('nav.home')" role="button" :aria-label="$t('nav.home')" tabindex="0"></a>
      <a @click="navigateToInfra()" 
         class="mdi mdi-server-network desktop-only" 
         :class="{ 'active': activeSection === 'infra' }"
         v-tooltip="$t('nav.infrastructure')" role="button" :aria-label="$t('nav.infrastructure')" tabindex="0"></a>
      <a @click="navigateToProjects()" 
         class="mdi mdi-rocket-launch-outline desktop-only" 
         :class="{ 'active': activeSection === 'projects' }"
         v-tooltip="$t('nav.projects')" role="button" :aria-label="$t('nav.projects')" tabindex="0"></a>
      <a @click="navigateToAbout(); closeMenu()" 
         class="mdi mdi-information" 
         :class="{ 'active': activeSection === 'about' }"
         v-tooltip="$t('nav.about')" role="button" :aria-label="$t('nav.about')" tabindex="0"></a>
      <a @click="navigateToSkills(); closeMenu()" 
         class="mdi mdi-account-cog" 
         :class="{ 'active': activeSection === 'skills' }"
         v-tooltip="$t('nav.skills')" role="button" :aria-label="$t('nav.skills')" tabindex="0"></a>
      <a @click="navigateToResume(); closeMenu()" 
         class="mdi mdi-file-document"
         :class="{ 'active': activeSection === 'resume' }"
         v-tooltip="$t('nav.cv')" role="button" :aria-label="$t('nav.cv')" tabindex="0"></a></div>
    <button @click="toggleTheme" class="theme-toggle-btn" :aria-label="colorStore.isLightTheme ? 'Switch to dark mode' : 'Switch to light mode'">
      <span v-if="colorStore.isLightTheme" class="mdi mdi-weather-night"></span>
      <span v-else class="mdi mdi-white-balance-sunny"></span>
    </button>
    <button @click="toggleLocale" class="locale-toggle-btn" v-tooltip="localeStore.isFrench ? 'Switch to English' : 'Passer en Français'" :aria-label="localeStore.isFrench ? 'Switch to English' : 'Passer en Français'">
      <span class="locale-text">{{ localeStore.currentLocale.toUpperCase() }}</span>
    </button>
    <div class="bottom">
      <a href="https://www.linkedin.com/in/rapha%C3%ABl-girardy-623b4a265/" target="_blank" class="mdi mdi-linkedin"
         v-tooltip="'LinkedIn'" aria-label="LinkedIn profile" rel="noopener noreferrer" @click="trackSocialClick('linkedin')"></a>
      <a href="https://github.com/EvantAiI" target="_blank" class="mdi mdi-github" v-tooltip="'GitHub'" aria-label="GitHub profile" rel="noopener noreferrer" @click="trackSocialClick('github')"></a>
      <a href="mailto:raphael.girardy@outlook.com" class="mdi mdi-gmail" v-tooltip="'Mail'" aria-label="Send email" @click="trackSocialClick('email')"></a>
    </div>
  </header>
</template>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 80px;
  height: 100%;
  background: var(--ln-glass-navbar);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-right: 1px solid var(--ln-glass-border);
  color: var(--ln-text-primary);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 30px 0;
  z-index: 1000;
  box-shadow: 4px 0 24px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

.header .top, .header .bottom {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

/* Glass pill buttons */
.header a {
  color: var(--ln-text-primary) !important; /* Force dark text color for icons */
  width: 52px;
  height: 52px;
  margin: 6px 0;
  font-size: 22px;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ln-radius-sm);
  background: var(--ln-glass-elevated); /* More opaque background */
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 4px 12px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
}

.header a:hover {
  cursor: pointer;
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 8px 20px var(--ln-glass-shadow-elevated);
  border-color: var(--ln-glass-border-strong);
}

.header a:active {
  transform: translateY(-1px) scale(1.02);
  box-shadow: 0 2px 8px var(--ln-glass-shadow);
}

/* Color variations on hover */
.header a.mdi-home:hover {
  color: var(--ln-teal-deep);
  background: var(--ln-glass-elevated);
}

.header a.mdi-server-network:hover {
  color: var(--ln-teal-deep);
  background: var(--ln-glass-elevated);
}

.header a.mdi-rocket-launch-outline:hover {
  color: var(--ln-teal-deep);
  background: var(--ln-glass-elevated);
}

.header a.mdi-information:hover {
  color: var(--ln-teal-light);
  background: var(--ln-glass-elevated);
}

.header a.mdi-account-cog:hover {
  color: var(--ln-terracotta-subtle);
  background: var(--ln-glass-elevated);
}

.header a.mdi-file-download:hover {
  color: var(--ln-terracotta-subtle);
  background: var(--ln-glass-elevated);
}

.header a.mdi-linkedin:hover {
  color: var(--ln-teal-deep);
  background: var(--ln-glass-elevated);
}

.header a.mdi-github:hover {
  color: var(--ln-stone-grey);
  background: var(--ln-glass-elevated);
}

.header a.mdi-gmail:hover {
  color: var(--ln-terracotta-subtle);
  background: var(--ln-glass-elevated);
}

/* Active state highlight - Placed at the end to override hover styles */
.header a.active {
  background: var(--ln-teal-deep) !important;
  color: white !important;
  border-color: var(--ln-teal-light) !important;
  box-shadow: 0 4px 15px rgba(44, 122, 123, 0.4) !important;
}

.header a.active:hover {
  background: var(--ln-teal-light) !important;
  color: white !important;
}

/* Theme toggle button */
.theme-toggle-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--ln-glass-surface);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--ln-glass-border);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  color: var(--ln-text-primary);
  font-size: 22px;
  position: relative;
  overflow: hidden;
  margin: 8px 0;
}

/* Liquid fill effect on theme button */
.theme-toggle-btn::before {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, var(--ln-teal-deep), var(--ln-teal-light));
  transition: top var(--ln-transition-slow) var(--ln-ease-out);
  z-index: 0;
}

.theme-toggle-btn:hover::before {
  top: 0;
}

.theme-toggle-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 8px 20px var(--ln-glass-shadow-elevated);
  border-color: var(--ln-glass-border-strong);
}

.theme-toggle-btn:active {
  transform: scale(1.02);
  box-shadow: 0 2px 8px var(--ln-glass-shadow);
}

.theme-toggle-btn span {
  color: var(--ln-teal-deep);
  transition: transform var(--ln-transition-normal) var(--ln-ease-smooth), color var(--ln-transition-fast) ease;
  position: relative;
  z-index: 1;
}

.theme-toggle-btn:hover span {
  transform: rotate(20deg);
  color: var(--ln-text-primary);
}

/* Locale toggle button */
.locale-toggle-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--ln-glass-surface);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--ln-glass-border);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  color: var(--ln-text-primary);
  font-size: 14px;
  font-weight: 700;
  position: relative;
  overflow: hidden;
  margin: 8px 0;
}

/* Liquid fill effect on locale button */
.locale-toggle-btn::before {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, var(--ln-terracotta-subtle), var(--ln-terracotta-hover));
  transition: top var(--ln-transition-slow) var(--ln-ease-out);
  z-index: 0;
}

.locale-toggle-btn:hover::before {
  top: 0;
}

.locale-toggle-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 8px 20px var(--ln-glass-shadow-elevated);
  border-color: var(--ln-glass-border-strong);
}

.locale-toggle-btn:active {
  transform: scale(1.02);
  box-shadow: 0 2px 8px var(--ln-glass-shadow);
}

.locale-text {
  color: var(--ln-terracotta-subtle);
  transition: color var(--ln-transition-fast) ease;
  position: relative;
  z-index: 1;
}

.locale-toggle-btn:hover .locale-text {
  color: var(--ln-text-primary);
}

/* Burger button (hidden on desktop) */
.burger-btn {
  display: none;
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 1100;
  width: 50px;
  height: 50px;
  background: var(--ln-glass-elevated);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border: 1px solid var(--ln-glass-border);
  border-radius: var(--ln-radius-sm);
  cursor: pointer;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  box-shadow: 0 4px 12px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

.burger-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px var(--ln-glass-shadow-elevated);
}

/* Hide burger button when menu is open on mobile */
@media (max-width: 768px) {
  .burger-btn.menu-open {
    opacity: 0;
    pointer-events: none;
  }
}

.burger-line {
  width: 24px;
  height: 2px;
  background: var(--ln-text-primary);
  border-radius: 2px;
  transition: all 0.3s ease;
}

.burger-line.open:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.burger-line.open:nth-child(2) {
  opacity: 0;
}

.burger-line.open:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* Backdrop (hidden on desktop) */
.backdrop {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.backdrop.active {
  opacity: 1;
  pointer-events: all;
}

/* Mobile responsive */
@media (max-width: 768px) {
  /* Show burger button */
  .burger-btn {
    display: flex;
  }

  /* Show backdrop when menu is open */
  .backdrop {
    display: block;
  }

  /* Transform header into slide-in menu */
  .header {
    width: 80px;
    transform: translateX(-100%);
    transition: transform 0.3s var(--ln-ease-smooth);
  }

  .header.mobile-open {
    transform: translateX(0);
  }

  .desktop-only {
    display: none !important;
  }

  .header a,
  .theme-toggle-btn,
  .locale-toggle-btn {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }
}
</style>